/**
 * The bridge between the rich message editor's DOM and the plain string a
 * message `body` actually is.
 *
 * Messages are stored and sent as text, not HTML — the send endpoints take a
 * string and nothing server-side sanitises markup. So the editor's formatting
 * is serialised to the same lightweight markdown the rest of the app already
 * uses for authored copy (see FormattedText / htmlToMarkdown), extended with
 * the marks the message toolbar offers:
 *
 *   **bold**      _italic_      ~~strikethrough~~
 *   `inline code`  ```fenced code blocks```
 *   - bullets      [label](https://url)
 *
 * Keeping the wire format as text means no backend change, and an older client
 * that hasn't got MessageBody yet degrades to visible markers rather than
 * showing raw HTML.
 */

/** Inserted as a caret anchor inside an otherwise-empty <code>; never sent. */
const ZERO_WIDTH_RE = /\u200B/g;

const BULLET_RE = /^\s*[-*]\s+/;
const FENCE_RE = /^\s*```/;

// ─────────────────────────────────────────────────────────────────────────────
// DOM → markdown
// ─────────────────────────────────────────────────────────────────────────────

function indexAmongSiblings(el: HTMLElement): number {
  return Array.from(el.parentElement?.children ?? []).indexOf(el);
}

/** Wraps `text` in a marker, leaving surrounding whitespace outside it —
 *  `**bold **` is not bold in any markdown dialect. */
function wrapMark(text: string, marker: string): string {
  const match = text.match(/^(\s*)([\s\S]*?)(\s*)$/);
  if (!match || !match[2]) return text;
  const [, lead, core, trail] = match;
  // Nested <b><b>x</b></b> (a real browser artifact) must not double up.
  if (core.startsWith(marker) && core.endsWith(marker)) return text;
  return `${lead}${marker}${core}${marker}${trail}`;
}

/** Text content of a <pre>, keeping the line structure. `textContent` alone
 *  can't be used: depending on the browser, Enter inside a code block inserts
 *  a <br> or a <div>, neither of which contributes to textContent — the
 *  newlines would silently vanish on send. */
function preText(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? "";
  if (node.nodeType !== Node.ELEMENT_NODE) return "";

  const el = node as HTMLElement;
  if (el.tagName === "BR") return "\n";

  const inner = Array.from(el.childNodes).map(preText).join("");
  return el.tagName === "DIV" || el.tagName === "P" ? `${inner}\n` : inner;
}

/** The text of a code block, line structure intact. Used by the editor when a
 *  code block is toggled back off and its lines become ordinary ones. */
export function codeBlockText(pre: HTMLElement): string {
  return preText(pre).replace(ZERO_WIDTH_RE, "").replace(/\n+$/, "");
}

function walk(node: ChildNode): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return (node.textContent ?? "").replace(ZERO_WIDTH_RE, "");
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return "";

  const el = node as HTMLElement;

  // Handled before the generic recursion: everything inside a code block is
  // literal, so no inline marks are emitted for its children.
  if (el.tagName === "PRE") {
    const code = preText(el).replace(ZERO_WIDTH_RE, "").replace(/\n+$/, "");
    return code ? `\n\`\`\`\n${code}\n\`\`\`\n` : "";
  }

  const inner = Array.from(el.childNodes).map(walk).join("");

  switch (el.tagName) {
    case "CODE":
      // A collapsed <code> the user opened and never typed into serialises to
      // nothing rather than an empty pair of backticks.
      return inner.trim() ? `\`${inner.replace(/\n/g, " ")}\`` : inner;
    case "B":
    case "STRONG":
      return wrapMark(inner, "**");
    case "I":
    case "EM":
      return wrapMark(inner, "_");
    case "S":
    case "STRIKE":
    case "DEL":
      return wrapMark(inner, "~~");
    case "A": {
      const href = el.getAttribute("href") ?? "";
      const label = inner.trim();
      if (!href) return inner;
      // A bare URL needs no [label](url) wrapper — MessageBody linkifies it.
      if (!label || label === href) return href;
      return `[${label}](${href})`;
    }
    case "LI": {
      const prefix = el.parentElement?.tagName === "OL" ? `${indexAmongSiblings(el) + 1}. ` : "- ";
      return `\n${prefix}${inner.trim()}`;
    }
    case "UL":
    case "OL":
      return `\n${inner}\n`;
    case "BR":
      return "\n";
    case "P":
    case "DIV":
      return `\n${inner.replace(/^\n+|\n+$/g, "")}\n`;
    default:
      return inner;
  }
}

/** Collapses the blank-line runs a contenteditable produces, without touching
 *  the inside of a fenced block — there, a blank line is part of the code. */
function normalize(raw: string): string {
  return raw
    .split(/(```[\s\S]*?```)/g)
    .map((segment, index) =>
      index % 2 === 1 ? segment : segment.replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n"),
    )
    .join("")
    .trim();
}

/** Serialises the editor's markup to the message markdown that gets sent. */
export function editorHtmlToMarkdown(html: string): string {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return normalize(Array.from(doc.body.childNodes).map(walk).join(""));
}

// ─────────────────────────────────────────────────────────────────────────────
// markdown → DOM
// ─────────────────────────────────────────────────────────────────────────────

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Makes a user-typed link safe to put in an href, or rejects it.
 *
 * A composer that accepts whatever is typed would happily build
 * `javascript:...` links that fire in the recipient's session, so only http,
 * https and mailto survive; a bare `acme.com` is assumed to be https.
 */
export function normalizeUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (/^(https?:\/\/|mailto:)/i.test(trimmed)) return trimmed;
  // Anything else carrying a scheme (javascript:, data:, vbscript:, …) is out.
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return null;
  if (/^[\w-]+(\.[\w-]+)+([/?#].*)?$/.test(trimmed)) return `https://${trimmed}`;
  return null;
}

/** One inline token: code span, bold, strike, italic, explicit link, bare URL. */
const INLINE_RE =
  /(`[^`\n]+`)|(\*\*[\s\S]+?\*\*)|(~~[\s\S]+?~~)|(_[^_\n]+_)|(\[[^\]\n]+\]\([^)\s]+\))|(https?:\/\/[^\s<]+)/g;

export interface InlineToken {
  kind: "text" | "code" | "bold" | "italic" | "strike" | "link";
  /** For text/code the literal content; for marks the inner markdown. */
  content: string;
  /** Only set on `link`. */
  href?: string;
}

/**
 * Splits a line into inline tokens. Shared by the editor's markdown→HTML seed
 * and by MessageBody, so what the composer shows and what the bubble renders
 * can't drift apart.
 *
 * Mark contents are returned as raw markdown, so callers recurse to get nested
 * marks (`**_both_**`) right.
 *
 * `links: false` is what makes that recursion terminate, and it is required
 * when tokenizing a link's own label: a bare URL tokenizes to a link whose
 * label IS the URL, so a caller recursing into it with links still enabled
 * would tokenize the same URL forever. Links don't nest in markdown either.
 */
export function tokenizeInline(line: string, { links = true } = {}): InlineToken[] {
  const tokens: InlineToken[] = [];
  let cursor = 0;

  INLINE_RE.lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = INLINE_RE.exec(line)) !== null) {
    const [raw] = match;
    const start = match.index;

    // CommonMark's intraword rule: `snake_case_name` and file_name_here are
    // not italics. Without this, ordinary identifiers in a message would come
    // out slanted with their underscores eaten.
    if (raw.startsWith("_")) {
      const before = line[start - 1] ?? "";
      const after = line[start + raw.length] ?? "";
      if (/\w/.test(before) || /\w/.test(after)) continue;
    }

    const isLink = raw.startsWith("[") || raw.startsWith("http");
    if (isLink && !links) continue;

    if (start > cursor) tokens.push({ kind: "text", content: line.slice(cursor, start) });
    cursor = start + raw.length;

    if (raw.startsWith("`")) {
      tokens.push({ kind: "code", content: raw.slice(1, -1) });
    } else if (raw.startsWith("**")) {
      tokens.push({ kind: "bold", content: raw.slice(2, -2) });
    } else if (raw.startsWith("~~")) {
      tokens.push({ kind: "strike", content: raw.slice(2, -2) });
    } else if (raw.startsWith("_")) {
      tokens.push({ kind: "italic", content: raw.slice(1, -1) });
    } else if (raw.startsWith("[")) {
      const split = raw.indexOf("](");
      const label = raw.slice(1, split);
      const href = normalizeUrl(raw.slice(split + 2, -1));
      if (href) tokens.push({ kind: "link", content: label, href });
      else tokens.push({ kind: "text", content: raw });
    } else {
      const href = normalizeUrl(raw);
      if (href) tokens.push({ kind: "link", content: raw, href });
      else tokens.push({ kind: "text", content: raw });
    }
  }

  if (cursor < line.length) tokens.push({ kind: "text", content: line.slice(cursor) });
  return tokens;
}

/** Inline markdown → markup, with no surrounding block element. The editor
 *  uses it for a single-line paste, which must land in the current line rather
 *  than starting a new block. */
export function inlineMarkdownToHtml(line: string, { links = true } = {}): string {
  return tokenizeInline(line, { links })
    .map((token) => {
      switch (token.kind) {
        case "code":
          return `<code>${escapeHtml(token.content)}</code>`;
        case "bold":
          return `<strong>${inlineMarkdownToHtml(token.content, { links })}</strong>`;
        case "italic":
          return `<em>${inlineMarkdownToHtml(token.content, { links })}</em>`;
        case "strike":
          return `<s>${inlineMarkdownToHtml(token.content, { links })}</s>`;
        case "link":
          // Nested links are not a thing: the label renders as marks and text.
          return `<a href="${escapeHtml(token.href ?? "")}">${inlineMarkdownToHtml(token.content, {
            links: false,
          })}</a>`;
        default:
          return escapeHtml(token.content);
      }
    })
    .join("");
}

/**
 * Rebuilds editor markup from message markdown.
 *
 * Only used to seed the editor — when a draft is restored, or reset to empty
 * after a send. Typing never round-trips through here, so the caret is never
 * disturbed by it.
 */
export function markdownToEditorHtml(markdown: string): string {
  if (!markdown) return "";

  const lines = markdown.split("\n");
  const blocks: string[] = [];
  let i = 0;

  while (i < lines.length) {
    if (FENCE_RE.test(lines[i])) {
      const body: string[] = [];
      i += 1;
      while (i < lines.length && !FENCE_RE.test(lines[i])) {
        body.push(lines[i]);
        i += 1;
      }
      i += 1; // closing fence
      blocks.push(`<pre>${escapeHtml(body.join("\n")) || "<br>"}</pre>`);
      continue;
    }

    if (BULLET_RE.test(lines[i])) {
      const items: string[] = [];
      while (i < lines.length && BULLET_RE.test(lines[i])) {
        items.push(`<li>${inlineMarkdownToHtml(lines[i].replace(BULLET_RE, ""))}</li>`);
        i += 1;
      }
      blocks.push(`<ul>${items.join("")}</ul>`);
      continue;
    }

    if (!lines[i].trim()) {
      // A blank line abutting a list or a code block is markdown's block
      // separator, not an empty line the author typed — reproducing it would
      // open the editor with a stray gap that wasn't there when they sent it.
      if (isBlockSeparator(lines, i)) {
        i += 1;
        continue;
      }
      blocks.push("<div><br></div>");
      i += 1;
      continue;
    }

    blocks.push(`<div>${inlineMarkdownToHtml(lines[i])}</div>`);
    i += 1;
  }

  return blocks.join("");
}

/** True when the blank line at `index` only separates a list or fenced block
 *  from what surrounds it — on either side, since it can be the gap before a
 *  block or the one after its last line. */
function isBlockSeparator(lines: string[], index: number): boolean {
  const isBlockLine = (line?: string) => !!line && (FENCE_RE.test(line) || BULLET_RE.test(line));
  return isBlockLine(lines[index + 1]) || isBlockLine(lines[index - 1]);
}

/**
 * Flattens message markdown to bare text, for places that show a message in a
 * single truncated line (the conversation list previews) where markers would
 * read as noise rather than formatting.
 */
export function stripMessageMarkdown(markdown: string, { links = true } = {}): string {
  if (!markdown) return "";

  return markdown
    .replace(/```[\s\S]*?```/g, " ")
    .split("\n")
    .map((line) =>
      tokenizeInline(line.replace(BULLET_RE, ""), { links })
        .map((token) => {
          if (token.kind === "text") return token.content;
          // A link's label is its own text — recursing with links still on
          // would re-tokenize a bare URL into the same link forever.
          if (token.kind === "link") return stripMessageMarkdown(token.content, { links: false });
          return stripMessageMarkdown(token.content, { links });
        })
        .join(""),
    )
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}
